// TODO: Migrate to financeable-ui once the shared form fields move off MUI Grid.
import { Grid, TextField, Autocomplete } from "@mui/material";
import { CircularProgress } from "@financeable-com-au/financeable-ui";
import { ConditionFieldOptions } from "@/constants";
// @ts-expect-error: ignoring as it is existing js file
import generateArrayOfYears from "@/utils/generateArrayOfYears";
// @ts-expect-error: ignoring as it is existing js file
import AutoCompleteLoader from "@/components/Application/AutoCompleteLoader";
import { AssetMakeModelSearchProps } from "@/utils/interface";
import { Make, Model, NVIC, TypeOfSale, Variant } from "@/store/slices/types/applicationFormSlice";
import { getAssetFieldAnchorId } from "@/utils/commonFunctions";
import { ASSET_FIELD } from "./assetRequiredFields";
import FormAutoCompleteField from "../CommonComponents/FormAutoCompleteField";

/**
 * The cascading picker: each field narrows the next, ending in the full model name that resolves the
 * asset's specification. Every list is fetched on open, so nothing is loaded until it is needed.
 */
const AssetMakeModelSearch = ({
  asset,
  allValues,
  handle,
  getLocalAndLenderError,
  isDisableEdit,
}: AssetMakeModelSearchProps) => {
  const {
    _id: assetId,
    makeLoader = false,
    modelLoader = false,
    variantLoader = false,
    typeOfSale = "",
    NVICLoader = false,
    listOfMakes = [],
    listOfVariants = [],
    NVICList = [],
    listOfModels = [],
    searchVehicleLoader = false,
    nvicLoaderData = false,
  } = asset;

  /** Anchor ids let the "Requires ..." hints scroll straight to the field that is still empty. */
  const anchorId = (fieldName: string) => getAssetFieldAnchorId(fieldName, assetId) || undefined;

  /** A dependent field shows no error until the field it depends on has been answered. */
  const dependentError = (fieldName: string, hasOptions: boolean, hasParent: boolean) =>
    !hasOptions && !hasParent ? undefined : getLocalAndLenderError({ fieldName });

  /**
   * A dependent picker unlocks once the field above it is answered or its own list has arrived, and
   * it stays unlocked while that list is being fetched. MUI blurs and closes a focused Autocomplete
   * the moment it is disabled, so letting the in-flight request disable the field — `pending` empties
   * the list it is gated on — would shut the dropdown under the broker and make the next keystroke
   * open it, and refetch, all over again.
   */
  const isDependentFieldDisabled = (hasOptions: boolean, hasParent: boolean, isLoading: boolean) =>
    searchVehicleLoader || (!isLoading && !hasOptions && !hasParent) || isDisableEdit;

  return (
    <Grid container direction="column">
      <Grid container item xs={12} direction="row" spacing={1}>
        <FormAutoCompleteField
          id={anchorId(ASSET_FIELD.condition)}
          fieldName="condition"
          label="Condition"
          value={allValues?.condition || ""}
          options={ConditionFieldOptions}
          error={!!getLocalAndLenderError({ fieldName: "condition" })}
          helperText={getLocalAndLenderError({ fieldName: "condition" })}
          disabled={searchVehicleLoader || typeOfSale === TypeOfSale.PrivateSale || isDisableEdit}
          onChange={handle.onChangeField}
          getOptionLabel={(option) => option || ""}
          gridProps={{ md: 2, sm: 12, xs: 12 }}
          variant="outlined"
          size="small"
        />

        <FormAutoCompleteField
          fieldName="ageOfAsset"
          label="Year of manufacture"
          value={allValues?.ageOfAsset?.toString() || ""}
          options={generateArrayOfYears()}
          error={!!getLocalAndLenderError({ fieldName: "ageOfAsset" })}
          helperText={getLocalAndLenderError({ fieldName: "ageOfAsset" })}
          disabled={searchVehicleLoader || !allValues?.assetTypeValue || isDisableEdit}
          onChange={handle.onChangeField}
          getOptionLabel={(option) => option || ""}
          gridProps={{ md: 2, sm: 12, xs: 12 }}
          variant="outlined"
          size="small"
        />

        <Grid item md={2.5} sm={3}>
          <Autocomplete
            id={anchorId(ASSET_FIELD.glassesMake)}
            loading={makeLoader}
            loadingText={<AutoCompleteLoader />}
            value={allValues?.glassesMake || ""}
            options={
              Array.isArray(listOfMakes)
                ? (listOfMakes as unknown as Make[]).map((make: Make) => ({
                    label: make?.name,
                    value: make?.code,
                  }))
                : []
            }
            onOpen={() => {
              handle.getListOfMakes();
            }}
            onChange={(event, newValue) => {
              handle.onChangeField(newValue, "glassesMake"); // clear fields if we update value of this
            }}
            disabled={searchVehicleLoader || isDisableEdit}
            size="small"
            getOptionLabel={(option) => option.label || ""}
            renderInput={(params) => (
              <TextField
                error={!!getLocalAndLenderError({ fieldName: "glassesMake" })}
                helperText={getLocalAndLenderError({ fieldName: "glassesMake" })}
                {...params}
                inputProps={{
                  ...params.inputProps,
                  value: params.inputProps.value === "undefined" ? "" : params.inputProps.value,
                }}
                label="Make"
                variant="outlined"
              />
            )}
          />
        </Grid>

        <Grid item md={2.5} sm={3}>
          <Autocomplete
            id={anchorId(ASSET_FIELD.glassesModel)}
            loading={modelLoader}
            loadingText={<AutoCompleteLoader />}
            disabled={isDependentFieldDisabled(
              Boolean(listOfModels?.[0]),
              Boolean(allValues?.glassesMake?.label),
              modelLoader,
            )}
            value={allValues?.glassesModel || ""}
            options={
              Array.isArray(listOfModels) && listOfModels.length > 0
                ? (listOfModels as unknown as Model[]).map((model: Model) => ({
                    label: model?.Name || null,
                    value: model?.Code || null,
                  }))
                : []
            }
            onOpen={() => {
              handle.getListOfModels();
            }}
            onChange={(event, newValue) => {
              handle.onChangeField(newValue, "glassesModel");
            }}
            size="small"
            getOptionLabel={(option) => option?.label || ""}
            renderInput={(params) => (
              <TextField
                error={
                  !!dependentError("glassesModel", Boolean(listOfModels?.[0]), Boolean(allValues?.glassesMake?.label))
                }
                helperText={dependentError(
                  "glassesModel",
                  Boolean(listOfModels?.[0]),
                  Boolean(allValues?.glassesMake?.label),
                )}
                {...params}
                label="Model"
                variant="outlined"
              />
            )}
          />
        </Grid>

        <Grid item md={3} sm={3}>
          <Autocomplete
            id={anchorId(ASSET_FIELD.glassesVariant)}
            loading={variantLoader}
            loadingText={<AutoCompleteLoader />}
            disabled={isDependentFieldDisabled(
              Boolean(listOfVariants?.[0]),
              Boolean(allValues?.glassesModel?.label),
              variantLoader,
            )}
            value={allValues?.glassesVariant || ""}
            options={
              Array.isArray(listOfVariants) && listOfVariants.length > 0
                ? (listOfVariants as unknown as Variant[]).map((variant: Variant) => ({
                    label: variant?.Name || "",
                    value: variant?.Name || "",
                  }))
                : []
            }
            onOpen={() => {
              handle.getListOfVariants();
            }}
            onChange={(event, newValue) => {
              handle.onChangeField(newValue, "glassesVariant");
            }}
            size="small"
            getOptionLabel={(option) => option?.label || ""}
            renderInput={(params) => (
              <TextField
                error={
                  !!dependentError(
                    "glassesVariant",
                    Boolean(listOfVariants?.[0]),
                    Boolean(allValues?.glassesModel?.label),
                  )
                }
                helperText={dependentError(
                  "glassesVariant",
                  Boolean(listOfVariants?.[0]),
                  Boolean(allValues?.glassesModel?.label),
                )}
                {...params}
                label="Variant"
                variant="outlined"
              />
            )}
          />
        </Grid>
      </Grid>

      <Grid container direction="row" style={{ margin: "10px 0 0px" }}>
        <Grid item md={12} sm={11}>
          <Autocomplete
            loading={NVICLoader}
            loadingText={<AutoCompleteLoader />}
            disabled={isDependentFieldDisabled(
              Boolean(NVICList?.[0]),
              Boolean(allValues?.glassesVariant?.label),
              NVICLoader,
            )}
            value={allValues?.glassesNVIC || ""}
            options={
              (NVICList?.length > 0 &&
                (NVICList as unknown as NVIC[])?.map((model: NVIC) => ({
                  label: model?.ModelName,
                  NVIC: model?.NVIC_CUR,
                  NVIC_MODEL: model?.NVIC_MODEL,
                }))) ||
              []
            }
            onOpen={() => {
              handle.getNVIC();
            }}
            onChange={(event, newValue) => {
              handle.applyVehicleSelection({ NVIC: newValue?.NVIC, label: newValue?.label });
            }}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                error={
                  !!dependentError("glassesNVIC", Boolean(NVICList?.[0]), Boolean(allValues?.glassesVariant?.label))
                }
                helperText={dependentError(
                  "glassesNVIC",
                  Boolean(NVICList?.[0]),
                  Boolean(allValues?.glassesVariant?.label),
                )}
                inputProps={{
                  ...params.inputProps,
                  value: params.inputProps.value === "" ? "" : params.inputProps.value,
                }}
                InputProps={{
                  ...params.InputProps,
                  // Picking is a round trip for the specification and its valuation. The spinner rides
                  // in the adornment rather than disabling the field, which would blur and close it.
                  endAdornment: (
                    <>
                      {nvicLoaderData ? <CircularProgress size="sm" /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                label="Full model name"
                variant="outlined"
              />
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AssetMakeModelSearch;
